import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { PaymentModal } from '../components/PaymentModal';
import { 
  CreditCard, 
  DollarSign,
  Check,
  Clock,
  AlertCircle,
  ArrowRight,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Payment = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentType, setPaymentType] = useState('deposit');
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const paidRef = useRef(false);

  // Check for returning from Stripe
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchBooking();
    fetchPaypalConfig();
  }, [bookingId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchPaypalConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/paypal/client-id`);
      setPaypalConfig(res.data);
    } catch (e) {
      setPaypalConfig({ configured: false, client_id: null });
    }
  };

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
      setBooking(response.data);
      
      // Set payment type based on deposit status
      if (response.data.deposit_paid) {
        setPaymentType('balance');
      }
    } catch (error) {
      toast.error('Failed to load booking details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        fetchBooking(); // Refresh booking data
        navigate(`/booking/${bookingId}/success`);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleStripePayment = () => {
    // On-page Stripe Elements modal (no redirect).
    setPayModalOpen(true);
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      await axios.post(`${API_URL}/api/payments/paypal/capture`, {
        booking_id: bookingId,
        payment_type: paymentType,
        order_id: data.orderID
      });
      
      toast.success('Payment successful!');
      navigate(`/booking/${bookingId}/success`);
    } catch (error) {
      toast.error('Failed to capture payment');
    }
  };

  const getPaymentAmount = () => {
    if (!booking) return 0;
    if (paymentType === 'deposit') {
      return booking.deposit_amount;
    }
    return booking.deposit_paid 
      ? booking.balance_due - booking.deposit_amount 
      : booking.balance_due;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]"></div>
        </div>
      </div>
    );
  }

  const amount = getPaymentAmount();

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Payment Options */}
          <div>
            <h1 className="text-3xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Complete Your Payment
            </h1>
            <p className="text-[#6B6B7A] mb-8">
              {paymentType === 'deposit' 
                ? 'Pay your 50% deposit to confirm your booking'
                : 'Pay your remaining balance'
              }
            </p>

            {/* Payment Type Selection */}
            {!booking?.deposit_paid && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-[#1E1E24] mb-3">Payment Option</p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setPaymentType('deposit')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentType === 'deposit'
                        ? 'border-[#FF3B6B] bg-[#FF3B6B]/5'
                        : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50'
                    }`}
                    data-testid="payment-type-deposit"
                  >
                    <p className="font-semibold text-[#1E1E24]">Deposit (50%)</p>
                    <p className="text-2xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      ${booking?.deposit_amount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-[#6B6B7A] mt-1">Balance due 7 days before</p>
                  </button>
                  <button
                    onClick={() => setPaymentType('full')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentType === 'full'
                        ? 'border-[#FF3B6B] bg-[#FF3B6B]/5'
                        : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50'
                    }`}
                    data-testid="payment-type-full"
                  >
                    <p className="font-semibold text-[#1E1E24]">Pay in Full</p>
                    <p className="text-2xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      ${booking?.total?.toFixed(2)}
                    </p>
                    <p className="text-xs text-[#6B6B7A] mt-1">Complete payment today</p>
                  </button>
                </div>
              </div>
            )}

            {/* Payment Method Selection */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-[#1E1E24] mb-3">Payment Method</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    paymentMethod === 'stripe'
                      ? 'border-[#FF3B6B] bg-[#FF3B6B]/5'
                      : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50'
                  }`}
                  data-testid="payment-method-stripe"
                >
                  <CreditCard className="w-6 h-6 text-[#6B6B7A]" />
                  <div className="text-left">
                    <p className="font-semibold text-[#1E1E24]">Credit Card</p>
                    <p className="text-xs text-[#6B6B7A]">Visa, MC, Amex</p>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    paymentMethod === 'paypal'
                      ? 'border-[#FF3B6B] bg-[#FF3B6B]/5'
                      : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50'
                  }`}
                  data-testid="payment-method-paypal"
                >
                  <DollarSign className="w-6 h-6 text-[#6B6B7A]" />
                  <div className="text-left">
                    <p className="font-semibold text-[#1E1E24]">PayPal</p>
                    <p className="text-xs text-[#6B6B7A]">Pay with PayPal</p>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMethod('pay_later')}
                  className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    paymentMethod === 'pay_later'
                      ? 'border-[#FF7A2E] bg-[#FF7A2E]/5'
                      : 'border-[#E8E5DF] hover:border-[#FF7A2E]/50'
                  }`}
                  data-testid="payment-method-pay-later"
                >
                  <CalendarClock className="w-6 h-6 text-[#6B6B7A]" />
                  <div className="text-left">
                    <p className="font-semibold text-[#1E1E24]">Pay Later</p>
                    <p className="text-xs text-[#6B6B7A]">Settle before the party</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Payment Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-6"
            >
              {paymentMethod === 'stripe' ? (
                <Button
                  onClick={handleStripePayment}
                  disabled={processing}
                  className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-lg btn-hover"
                  data-testid="pay-stripe-btn"
                >
                  {processing ? (
                    <span className="flex items-center gap-2">
                      <Clock className="w-5 h-5 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Pay ${amount.toFixed(2)} CAD
                    </span>
                  )}
                </Button>
              ) : paymentMethod === 'pay_later' ? (
                <PayLaterPanel bookingId={bookingId} amount={amount} onSuccess={() => navigate(`/booking/${bookingId}/success`)} />
              ) : paypalConfig?.configured && paypalConfig?.client_id ? (
                <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: 'CAD', intent: 'capture' }}>
                  <PayPalButtons
                    style={{ layout: 'vertical', color: 'gold', shape: 'pill' }}
                    createOrder={async () => {
                      const res = await axios.post(`${API_URL}/api/payments/paypal/create-order`, {
                        booking_id: bookingId,
                        payment_type: paymentType,
                      });
                      return res.data.order_id;
                    }}
                    onApprove={handlePayPalApprove}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      toast.error('PayPal payment failed');
                    }}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="p-4 rounded-xl bg-[#FFF4E6] border border-[#FFD166] text-sm text-[#8B6914]" data-testid="paypal-not-configured">
                  <p className="font-semibold mb-1">PayPal not configured</p>
                  <p>Please ask the site owner to add <code>PAYPAL_CLIENT_ID</code> and <code>PAYPAL_SECRET</code> in the backend <code>.env</code>, or pay with credit card instead.</p>
                </div>
              )}

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#6B6B7A]">
                <AlertCircle className="w-4 h-4" />
                <span>Secure payment powered by {paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}</span>
              </div>
            </motion.div>
          </div>

          {/* Booking Summary */}
          <div>
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Booking Summary
              </h3>

              <div className="space-y-4 mb-6">
                <div className="p-4 bg-[#FFFDF8] rounded-xl">
                  <p className="font-semibold text-[#1E1E24]">{booking?.package_name}</p>
                  <p className="text-sm text-[#6B6B7A]">
                    {new Date(booking?.date).toLocaleDateString('en-CA', { 
                      weekday: 'long', month: 'long', day: 'numeric' 
                    })}
                  </p>
                  <p className="text-sm text-[#6B6B7A]">
                    {booking?.time_slot === '10:00' ? '10:00 AM' : booking?.time_slot === '13:00' ? '1:00 PM' : '4:00 PM'}
                  </p>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Birthday Child</span>
                  <span className="text-[#1E1E24] font-medium">{booking?.child_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Guests</span>
                  <span className="text-[#1E1E24] font-medium">{booking?.guest_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Allergy Form</span>
                  <span className={`font-medium flex items-center gap-1 ${
                    booking?.allergy_form_completed ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'
                  }`}>
                    {booking?.allergy_form_completed ? (
                      <>
                        <Check className="w-4 h-4" /> Completed
                      </>
                    ) : (
                      'Pending'
                    )}
                  </span>
                </div>
              </div>

              <div className="border-t border-[#E8E5DF] pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Subtotal</span>
                  <span className="text-[#1E1E24]">${booking?.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B6B7A]">Tax (13%)</span>
                  <span className="text-[#1E1E24]">${booking?.tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t border-[#E8E5DF]">
                  <span className="text-[#1E1E24]">Total</span>
                  <span className="text-[#1E1E24]">${booking?.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Status */}
              <div className="mt-6 space-y-2">
                {booking?.deposit_paid && (
                  <div className="flex justify-between text-sm bg-[#06D6A0]/10 p-3 rounded-xl">
                    <span className="text-[#06D6A0] font-medium">Deposit Paid</span>
                    <span className="text-[#06D6A0] font-semibold">${booking?.deposit_amount?.toFixed(2)}</span>
                  </div>
                )}
                {!booking?.balance_paid && (
                  <div className="flex justify-between text-sm bg-[#FF3B6B]/10 p-3 rounded-xl">
                    <span className="text-[#FF3B6B] font-medium">
                      {paymentType === 'deposit' ? 'Deposit Due' : 'Balance Due'}
                    </span>
                    <span className="text-[#FF3B6B] font-semibold" data-testid="amount-due">
                      ${amount.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentModal
        open={payModalOpen}
        onOpenChange={(o) => {
          setPayModalOpen(o);
          if (!o && paidRef.current) {
            navigate(`/booking/${bookingId}/success`);
          }
        }}
        booking={booking ? { ...booking, booking_id: bookingId } : null}
        mode="customer"
        defaultAmount={amount}
        title={paymentType === 'deposit' ? 'Pay your deposit' : 'Pay your balance'}
        onSuccess={() => { paidRef.current = true; fetchBooking(); }}
      />
    </div>
  );
};

export default Payment;

// ─── Pay-Later panel ────────────────────────────────────────────
const PayLaterPanel = ({ bookingId, amount, onSuccess }) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const navigate = useNavigate();

  const handleConfirm = async () => {
    if (!acknowledged) {
      toast.error('Please confirm you understand the 24-hour hold rule.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/bookings/${bookingId}/defer-payment`, {
        acknowledged: true,
      });
      toast.success("Your slot is held — check your email for payment details.");
      // iter-135 — navigate to the "Booking Held" page (clearer copy than
      // the standard "Booking Confirmed" success page).
      navigate(`/booking/${bookingId}/held`, { replace: true });
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Pay Later hold. Please try another method.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFF8E6] border-2 border-[#FFD166] rounded-2xl p-5" data-testid="pay-later-panel">
      <div className="flex items-start gap-3 mb-4">
        <CalendarClock className="w-6 h-6 text-[#B8860B] mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-[#1E1E24]">Hold my slot for 24 hours</p>
          <p className="text-sm text-[#6B6B7A] mt-0.5">
            We'll reserve your party slot for the next 24 hours while you arrange payment. Pay at least the 50% deposit (or the full amount) before the hold expires and your booking is confirmed.
          </p>
        </div>
      </div>

      {/* Acknowledgment checkbox */}
      <label
        className="flex items-start gap-3 p-3 rounded-xl bg-white border-2 border-[#FFD166] cursor-pointer hover:border-[#FF7A2E] transition mb-4"
        data-testid="pay-later-acknowledgment"
      >
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#FF3B6B]"
          data-testid="pay-later-acknowledgment-checkbox"
        />
        <span className="text-sm text-[#1E1E24] leading-snug">
          I understand my booking is held for <strong>24 hours</strong>.
          I must pay <strong>50% deposit</strong> or the full amount to confirm.
          If I don't pay in time, the slot is released and my booking is automatically cancelled.
        </span>
      </label>

      <Button
        onClick={handleConfirm}
        disabled={submitting || !acknowledged}
        className="w-full bg-[#FF7A2E] hover:bg-[#E66A1E] text-white rounded-full py-5 text-lg disabled:opacity-40"
        data-testid="confirm-pay-later-btn"
      >
        {submitting ? (
          <span className="flex items-center gap-2"><Clock className="w-5 h-5 animate-spin" />Holding your slot…</span>
        ) : (
          <>Hold my slot for 24 hours</>
        )}
      </Button>
      <p className="text-xs text-[#6B6B7A] text-center mt-2">
        ${amount.toFixed(2)} CAD due to confirm — we'll email a payment link.
      </p>
    </div>
  );
};
